import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { gql, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { compact } from '@moda/portal-stanchions';
import { GiftFinderPageQuery, GiftFinderPageQueryVariables } from '../../generated/types';
import { DEFAULT_SORT_OPTIONS } from '../../constants';
import { findSectionModules } from '../../lib/findSectionModules';
import { usePLPParams } from '../../hooks/usePLPParams';
import { PUMO_MODULE_FRAGMENT } from '../../components/PumoModule';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import { EnableLazyLoad, LazyLoad } from '../../components/LazyLoad';
import { ModuleSection } from '../../components/ModuleSection';
import {
  GIFT_FINDER_NAVIGATION_FRAGMENT,
  GiftFinderNavigation
} from '../../components/GiftFinderNavigation';
import NotFoundPage from '../NotFoundPage';
import { useRuleVariables } from '../../hooks/useRuleVariables';
import { FacadePageType } from '../../lib/getRuleContextFromSlug';
import { useShowSaleOnPLP } from '../../hooks/useShowSaleOnPLP';

export const GIFT_FINDER_PAGE_QUERY: TypedDocumentNode<
  GiftFinderPageQuery,
  GiftFinderPageQueryVariables
> = gql`
  query GiftFinderPageQuery(
    $after: String!
    $attributes: [String]
    $availability: [AvailabilityInput]
    $categoryPath: String
    $countryCode: String
    $designers: [String]
    $dynamicReRanking: Boolean
    $filterColors: [String]
    $giftCategory: String
    $giftCategoryPath: String
    $materials: [String]
    $noSale: Boolean
    $onSale: Boolean
    $priceRange: PriceRange
    $ruleContexts: [String]
    $rules: [Rule]
    $markdownPercentages: [String]
    $scoring: String
    $section: String!
    $sizes: [String]
    $slug: String!
    $versionId: Int
  ) {
    displayPage(slug: $slug, version_id: $versionId) {
      slug
      name
      targetBuckets {
        name
        modules {
          ...PumoModuleFragment
        }
      }
      meta_description
    }
    giftCategories: gift_categories(
      country_code: $countryCode
      category: $giftCategory
      sale: $onSale
    ) {
      ...GiftFinderNavigationFragment
    }
    categoryResult: product_listing(
      input: {
        attributes: $attributes
        availability: $availability
        category_path: $categoryPath
        country_code: $countryCode
        designers: $designers
        dynamic_reranking: $dynamicReRanking
        filter_colors: $filterColors
        gift_category_path: $giftCategoryPath
        materials: $materials
        on_sale: $onSale
        no_sale: $noSale
        price_range: $priceRange
        rounded_markdown_percentages: $markdownPercentages
        sizes: $sizes
        version_id: $versionId
        scoring: $scoring
        rule_contexts: $ruleContexts
        rules: $rules
      }
      slug: $slug
      section: $section
    ) {
      pageName: page_name
      ...ProductListPageResultFragment
      ...PageFilterFragment
    }
  }
  ${PUMO_MODULE_FRAGMENT}
  ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
  ${GIFT_FINDER_NAVIGATION_FRAGMENT}
`;

export const GiftFinderPage: React.FC = () => {
  const { giftCategory, giftSubcategory } = useParams<{
    giftCategory?: string;
    giftSubcategory?: string;
  }>();

  const showSaleInPLPs = useShowSaleOnPLP();

  const { queryVariables } = usePLPParams();

  const ruleVariables = useRuleVariables(
    FacadePageType.Category,
    compact(['gifts', giftCategory, giftSubcategory]).join('/')
  );

  const {
    data: currentData,
    previousData,
    error,
    loading
  } = useQuery(GIFT_FINDER_PAGE_QUERY, {
    variables: {
      ...queryVariables,
      ...ruleVariables,
      giftCategory,
      giftCategoryPath: compact([giftCategory, giftSubcategory]).join('/'),
      slug: compact(['gifts', giftCategory]).join('/'),
      section: 'gifts',
      noSale: !showSaleInPLPs,
      onSale: queryVariables.onSale ?? (showSaleInPLPs ? undefined : false)
    }
  });

  const data = currentData ?? previousData;

  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);

  const category =
    giftCategory && data?.giftCategories.find(category => category.slug === giftCategory);

  const title = category ? `Gifts - ${category.label}` : 'All Gifts';

  const description = `Shop the latest${category && !category.label.startsWith('For ') ? ` ${category.label}` : ''} Gifts${category && category.label.startsWith('For ') ? ` ${category.label}` : ''} at Moda Operandi.`;

  if (data && !data.categoryResult) return <NotFoundPage />;

  return (
    <>
      {topModules && <ModuleSection modules={topModules.modules} />}
      {headerModules && <ModuleSection modules={headerModules.modules} />}

      <GiftFinderNavigation categories={data?.giftCategories ?? []} isLoading={loading} />

      <ProductListing
        title={title}
        description={description}
        headerText={title}
        queryResult={data?.categoryResult}
        error={error}
        availableSorts={DEFAULT_SORT_OPTIONS}
        loading={loading}
      />

      {bottomModules && (
        <EnableLazyLoad>
          <LazyLoad>
            <ModuleSection modules={bottomModules.modules} />
          </LazyLoad>
        </EnableLazyLoad>
      )}
    </>
  );
};
